import React, { useState, useEffect } from 'react';
import {
  TextField, Button, Box, FormControl, InputLabel, Select, MenuItem,
  Alert, CircularProgress
} from '@mui/material';
import { PhoneCall } from 'lucide-react';
import axios from '../utils/axios';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions } from './common/modal';

const CustomerEnquiryDataModal = ({ open, onClose, data, categories, countries, states, cities, onSuccess }) => {
  const isEdit = !!data;
  
  const [formData, setFormData] = useState({
    number: '',
    category: '',
    city: '',
    state: '',
    country: 'India'
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (data && open) {
      setFormData({
        number: data.number || '',
        category: data.category || '',
        city: data.city || '',
        state: data.state || '',
        country: data.country || ''
      });
    } else if (open) {
      setFormData({ 
        number: '',
        category: '',
        city: '',
        state: '',
        country: 'India'
      });
    }
    setError('');
  }, [data, open]);

  const handleChange = (e) => {
    if (e.target.name === 'state') {
      setFormData({ ...formData, state: e.target.value, city: '' });
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }
    if (error) setError('');
  };

  const norm = (str) => String(str || '').trim().toLowerCase();
  const selectedStateObj = (Array.isArray(states) ? states : []).find(
    s => norm(s.state_name) === norm(formData.state) || String(s._id) === String(formData.state)
  );
  const filteredCities = formData.state && Array.isArray(cities)
    ? Array.from(
        new Map(
          cities
            .filter(c => {
              if (norm(c.state?.state_name) === norm(formData.state)) return true;
              if (typeof c.state === 'string' && norm(c.state) === norm(formData.state)) return true;
              if (norm(c.state_name) === norm(formData.state)) return true;
              if (selectedStateObj && (
                String(c.state_id) === String(selectedStateObj._id) ||
                String(c.state_id?._id) === String(selectedStateObj._id) ||
                String(c.state?._id) === String(selectedStateObj._id)
              )) return true;
              return false;
            })
            .map(c => [norm(c.city_name), c])
        ).values()
      ).sort((a, b) => (a.city_name || '').localeCompare(b.city_name || ''))
    : (Array.isArray(cities) ? cities : []);

  const hasCurrentCityInFiltered = filteredCities.some(c => norm(c.city_name) === norm(formData.city));
  const displayCities = (!hasCurrentCityInFiltered && formData.city)
    ? [{ _id: 'current-city-fallback', city_name: formData.city }, ...filteredCities]
    : filteredCities;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.number) {
      setError('Phone Number is required');
      return;
    }
    
    setLoading(true);
    try {
      if (isEdit) {
        await axios.put(`/customer-enquiry-data/${data._id}`, formData, { skipNotify: 'success' });
      } else {
        await axios.post('/customer-enquiry-data', formData, { skipNotify: 'success' });
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.msg || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminDialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="sm"
    >
      <AdminDialogHeader
        title={isEdit ? 'Edit Customer Enquiry Data' : 'Add Customer Enquiry Data'}
        subtitle={isEdit ? 'Modify phone number and category/location targeting' : 'Add raw lead data for distribution'}
        icon={PhoneCall}
        onClose={!loading ? onClose : undefined}
      />
      
      <form onSubmit={handleSubmit}>
        <AdminDialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 0.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
              <TextField 
                label="Phone Number" 
                name="number" 
                value={formData.number} 
                onChange={handleChange} 
                fullWidth 
                required 
                placeholder="e.g. 9876543210"
              />
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select name="category" value={formData.category} label="Category" onChange={handleChange}>
                  {categories?.map((cat) => (
                    <MenuItem key={cat._id} value={cat.category_name}>{cat.category_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2.5 }}>
              <FormControl fullWidth>
                <InputLabel>Country</InputLabel>
                <Select name="country" value={formData.country} label="Country" onChange={handleChange}>
                  {countries?.map((c) => (
                    <MenuItem key={c._id} value={c.country_name}>{c.country_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>State</InputLabel>
                <Select name="state" value={formData.state} label="State" onChange={handleChange}>
                  {states?.map((s) => (
                    <MenuItem key={s._id} value={s.state_name}>{s.state_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth disabled={!formData.state}>
                <InputLabel>City</InputLabel>
                <Select name="city" value={formData.city} label="City" onChange={handleChange}>
                  {displayCities?.map((c) => (
                    <MenuItem key={c._id} value={c.city_name}>{c.city_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </AdminDialogContent>
        
        <AdminDialogActions>
          <Button onClick={onClose} color="inherit" disabled={loading} sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={loading}
            startIcon={loading && <CircularProgress size={16} color="inherit" />}
            sx={{ px: 3, borderRadius: 2, fontWeight: 700 }}
          >
            {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Record'}
          </Button>
        </AdminDialogActions>
      </form>
    </AdminDialog>
  );
};

export default CustomerEnquiryDataModal;

